import { useToast } from "@/hooks/use-toast";
import { useRef, useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import type { EncryptionAuditCounts, EncryptionAuditRun } from "@shared/schema";
import { parseErrorMessage } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

export function EncryptionAuditPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map());
  const [highlightedRunId, setHighlightedRunId] = useState<number | null>(null);

  function focusRunRow(runId: number) {
    const row = rowRefs.current.get(runId);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setHighlightedRunId(runId);
    window.setTimeout(() => {
      setHighlightedRunId((current) => (current === runId ? null : current));
    }, 2200);
  }

  const latestQuery = useQuery<{ latest: EncryptionAuditRun | null; live: EncryptionAuditCounts }>({
    queryKey: ["/api/admin/encryption-audit/latest"],
    queryFn: async () => {
      const res = await fetch("/api/admin/encryption-audit/latest", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load encryption audit");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const historyQuery = useQuery<EncryptionAuditRun[]>({
    queryKey: ["/api/admin/encryption-audit/history"],
    queryFn: async () => {
      const res = await fetch("/api/admin/encryption-audit/history?limit=20", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load audit history");
      return res.json();
    },
  });

  const runMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await fetch("/api/admin/encryption-audit/run", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Audit run failed");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Encryption audit ran", description: "Counts refreshed below." });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/encryption-audit/latest"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/encryption-audit/history"] });
    },
    onError: (err: Error) => {
      toast({ title: "Audit failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const live = latestQuery.data?.live;
  const latest = latestQuery.data?.latest;
  const liveTotal = live
    ? live.seamApiKey + live.propertyWifiPassword + live.paymentAccountNumber + live.paymentRoutingNumber
    : 0;

  function statusBadge(status: string | null | undefined) {
    if (status === "ok") return <Badge className="bg-green-100 text-green-800 border-green-300" data-testid="badge-encryption-status-ok">Clean</Badge>;
    if (status === "drift") return <Badge className="bg-red-100 text-red-800 border-red-300" data-testid="badge-encryption-status-drift">Drift</Badge>;
    if (status === "error") return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300" data-testid="badge-encryption-status-error">Error</Badge>;
    return <Badge variant="outline">{status ?? "—"}</Badge>;
  }

  const columns: Array<{ key: keyof EncryptionAuditCounts; label: string }> = [
    { key: "seamApiKey", label: "seam_connections.api_key" },
    { key: "propertyWifiPassword", label: "properties.wifi_password" },
    { key: "paymentAccountNumber", label: "payment_instructions_settings.account_number" },
    { key: "paymentRoutingNumber", label: "payment_instructions_settings.routing_number" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-encryption-audit-title">Encryption Drift Audit</h3>
          <p className="text-sm text-muted-foreground">
            Daily check that counts plaintext rows still sitting in encrypted columns. Drift can be introduced by raw SQL imports or restored backups.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => runMutation.mutate()}
          disabled={runMutation.isPending}
          data-testid="button-run-encryption-audit"
        >
          {runMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Run Audit Now
        </Button>
      </div>

      {latestQuery.isLoading ? (
        <Card className="p-6"><Skeleton className="h-20 w-full" /></Card>
      ) : (
        <Card className="p-6" data-testid="card-encryption-audit-live">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              {liveTotal === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <ShieldAlert className="h-5 w-5 text-red-600" />
              )}
              <span className="font-semibold">
                {liveTotal === 0
                  ? "All sensitive fields are encrypted"
                  : `${liveTotal} plaintext row(s) detected right now`}
              </span>
            </div>
            {statusBadge(liveTotal === 0 ? "ok" : "drift")}
            <span className="text-xs text-muted-foreground" data-testid="text-encryption-live-source">
              Live count (refreshes every minute)
            </span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Column</TableHead>
                <TableHead className="text-right">Plaintext rows (live)</TableHead>
                <TableHead className="text-right">Last cron run</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {columns.map((c) => {
                const liveValue = live ? live[c.key] : 0;
                const lastValue = latest
                  ? c.key === "seamApiKey"
                    ? latest.seamApiKeyPlaintext
                    : c.key === "propertyWifiPassword"
                      ? latest.propertyWifiPasswordPlaintext
                      : c.key === "paymentAccountNumber"
                        ? latest.paymentAccountNumberPlaintext
                        : latest.paymentRoutingNumberPlaintext
                  : null;
                return (
                  <TableRow key={c.key} data-testid={`row-encryption-${c.key}`}>
                    <TableCell className="font-mono text-xs">{c.label}</TableCell>
                    <TableCell
                      className={`text-right font-bold ${liveValue > 0 ? "text-red-600" : "text-green-600"}`}
                      data-testid={`text-encryption-live-${c.key}`}
                    >
                      {liveValue}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground" data-testid={`text-encryption-last-${c.key}`}>
                      {lastValue ?? "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {latest ? (
            <p className="text-xs text-muted-foreground mt-3" data-testid="text-encryption-last-run">
              Last run: {new Date(latest.ranAt).toLocaleString()} ({latest.triggeredBy})
              {latest.alertSentAt ? ` — alert emailed ${new Date(latest.alertSentAt).toLocaleString()}` : ""}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-3" data-testid="text-encryption-never-run">
              No scheduled run has executed yet. Click "Run Audit Now" to record the first run.
            </p>
          )}
        </Card>
      )}

      {(() => {
        const trendRuns = [...(historyQuery.data ?? [])].slice(0, 14).reverse();
        const trendMax = Math.max(1, ...trendRuns.map((r) => r.totalPlaintext));
        const driftRunCount = trendRuns.filter((r) => r.totalPlaintext > 0).length;
        const firstDate = trendRuns.length > 0
          ? new Date(trendRuns[0].ranAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
          : "";
        const lastDate = trendRuns.length > 0
          ? new Date(trendRuns[trendRuns.length - 1].ranAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })
          : "";
        return (
          <Card className="p-6" data-testid="card-encryption-trend">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <h4 className="font-semibold" data-testid="text-encryption-trend-title">Drift trend (last 14 runs)</h4>
                <p className="text-xs text-muted-foreground">
                  Total plaintext rows per audit run, oldest to newest. Red bars flag drift.
                </p>
              </div>
              {trendRuns.length > 0 && (
                <span
                  className={`text-xs font-medium ${driftRunCount > 0 ? "text-red-600" : "text-green-600"}`}
                  data-testid="text-encryption-trend-summary"
                >
                  {driftRunCount} of last {trendRuns.length} run{trendRuns.length === 1 ? "" : "s"} had drift
                </span>
              )}
            </div>
            {historyQuery.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : trendRuns.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="text-encryption-trend-empty">
                No runs recorded yet.
              </p>
            ) : (
              <>
                <div className="flex items-end gap-1 h-24" data-testid="chart-encryption-trend">
                  {trendRuns.map((r) => {
                    const drift = r.totalPlaintext > 0;
                    const heightPct = drift
                      ? Math.max(15, Math.round((r.totalPlaintext / trendMax) * 100))
                      : 4;
                    const tooltip = `${new Date(r.ranAt).toLocaleString()} — ${r.totalPlaintext} plaintext row(s). Click to view details.`;
                    const breakdown: Array<{ key: string; label: string; value: number }> = [
                      { key: "seamApiKey", label: "seam_api_key", value: r.seamApiKeyPlaintext },
                      { key: "wifiPassword", label: "wifi_password", value: r.propertyWifiPasswordPlaintext },
                      { key: "accountNumber", label: "account_number", value: r.paymentAccountNumberPlaintext },
                      { key: "routingNumber", label: "routing_number", value: r.paymentRoutingNumberPlaintext },
                    ];
                    return (
                      <HoverCard key={r.id} openDelay={120} closeDelay={80}>
                        <HoverCardTrigger asChild>
                          <button
                            type="button"
                            onClick={() => focusRunRow(r.id)}
                            className="flex flex-col items-center justify-end flex-1 min-w-0 h-full p-0 bg-transparent border-0 cursor-pointer rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 hover:opacity-80 transition-opacity"
                            title={tooltip}
                            aria-label={`Audit run on ${new Date(r.ranAt).toLocaleString()}: ${r.totalPlaintext} plaintext row(s). Activate to view details.`}
                            data-testid={`trend-bar-${r.id}`}
                          >
                            {drift && (
                              <span
                                className="block h-1.5 w-1.5 rounded-full bg-red-600 mb-1"
                                data-testid={`trend-dot-${r.id}`}
                              />
                            )}
                            <div
                              className={`w-full rounded-t ${drift ? "bg-red-500" : "bg-green-500"}`}
                              style={{ height: `${heightPct}%` }}
                              data-testid={`trend-bar-fill-${r.id}`}
                            />
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent
                          align="center"
                          className="w-72 text-xs"
                          data-testid={`trend-tooltip-${r.id}`}
                        >
                          <div className="font-semibold text-sm mb-1" data-testid={`trend-tooltip-time-${r.id}`}>
                            {new Date(r.ranAt).toLocaleString()}
                          </div>
                          <div
                            className={`mb-2 font-medium ${drift ? "text-red-600" : "text-green-600"}`}
                            data-testid={`trend-tooltip-total-${r.id}`}
                          >
                            {r.totalPlaintext === 0
                              ? "No plaintext rows"
                              : `${r.totalPlaintext} plaintext row(s)`}
                          </div>
                          <ul className="space-y-1">
                            {breakdown.map((b) => (
                              <li
                                key={b.key}
                                className="flex items-center justify-between gap-2 font-mono"
                                data-testid={`trend-tooltip-${b.key}-${r.id}`}
                              >
                                <span className="text-muted-foreground">{b.label}</span>
                                <span className={b.value > 0 ? "text-red-600 font-semibold" : "text-green-600"}>
                                  {b.value}
                                </span>
                              </li>
                            ))}
                          </ul>
                          <p className="text-[10px] text-muted-foreground mt-2">
                            Click bar to jump to this run in the table below.
                          </p>
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })}
                </div>
                <div className="flex justify-between text-[10px] text-muted-foreground mt-2">
                  <span data-testid="text-encryption-trend-first">{firstDate}</span>
                  <span data-testid="text-encryption-trend-last">{lastDate}</span>
                </div>
              </>
            )}
          </Card>
        );
      })()}

      <Card className="p-6" data-testid="card-encryption-history">
        <h4 className="font-semibold mb-3">Recent runs</h4>
        {historyQuery.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (historyQuery.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="text-encryption-history-empty">No runs recorded yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ran at</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead className="text-right">Total plaintext</TableHead>
                <TableHead>Alert</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(historyQuery.data ?? []).map((r) => (
                <TableRow
                  key={r.id}
                  ref={(el) => {
                    if (el) {
                      rowRefs.current.set(r.id, el);
                    } else {
                      rowRefs.current.delete(r.id);
                    }
                  }}
                  className={`transition-colors ${highlightedRunId === r.id ? "bg-yellow-100 dark:bg-yellow-900/40" : ""}`}
                  data-testid={`row-encryption-run-${r.id}`}
                  data-highlighted={highlightedRunId === r.id ? "true" : undefined}
                >
                  <TableCell className="text-sm">{new Date(r.ranAt).toLocaleString()}</TableCell>
                  <TableCell>{statusBadge(r.status)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.triggeredBy}</TableCell>
                  <TableCell
                    className={`text-right font-semibold ${r.totalPlaintext > 0 ? "text-red-600" : "text-green-600"}`}
                  >
                    {r.totalPlaintext}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {r.alertSentAt ? new Date(r.alertSentAt).toLocaleString() : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
